import { isEmbedded, useParentPostMessage } from '@yes.technology/react-toolkit'
import { loginSucceeded } from 'authentication/Login/ducks/authentication'
import jwtDecode from 'jwt-decode'
import { useCallback, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router'

import { IframeAttributes } from 'shared/components/Iframe'
import { getApplicationSystemInfo } from 'shared/utils'

type OriginalSender = {
  source: string
}

type EventDataMessage = {
  height?: string
  type?: string
  url?: string
  token?: string
  expirationDate?: number
  finishedInteraction?: boolean
  originalSender: OriginalSender
}

type UseIframeProps = {
  origin: string
  src: string
  title?: string
  allow?: string
  initialHeight?: string
  sharedState?: Record<string, unknown>
  returnOnFinishedInteraction?: boolean
  interactionReturnUrl?: string
}

const useIframe = ({
  origin,
  src,
  title = 'Masterdata',
  allow = 'camera;geolocation',

  interactionReturnUrl,
  returnOnFinishedInteraction = false,

  initialHeight = '800px', //  TODO: Alterar para 100vh quando a workflow engine passar a enviar o height por mensagem
  sharedState
}: UseIframeProps) => {
  const [height, setHeight] = useState(initialHeight)
  const [iframeAttributes, setIframeAttributes] = useState<
    IframeAttributes | undefined
  >()

  const dispatch = useDispatch()

  const navigate = useNavigate()

  const { postMessage } = useParentPostMessage()

  const redirectToUrl = useCallback(
    (url?: string) => {
      const absoluteUrlRegex = new RegExp('^(?:[a-z+]+:)?//', 'i')
      if (url) {
        absoluteUrlRegex.test(url)
          ? (globalThis.location.href = url)
          : navigate(url)
      }
    },
    [navigate]
  )

  const receiveMessage = useCallback(
    (event: MessageEvent<EventDataMessage>) => {
      // TODO: Improve iframe recognition in case of two different iframes with same origin (URL =/= Origin)
      if (!origin.startsWith(event.origin)) return false
      const { height, type, originalSender, finishedInteraction } = event.data

      if (height) {
        setHeight(`${Math.ceil(Number(height) + 50)}`)

        return
      }

      if (type === 'redirect') {
        if (isEmbedded()) {
          postMessage(event.data)
        } else {
          redirectToUrl(event.data.url)
        }

        return
      }

      if (type === 'updateAuthToken') {
        const { token, expirationDate } = event.data
        if (!token || !expirationDate) return

        const decoded = jwtDecode<{ email: string }>(token)
        dispatch(
          loginSucceeded(decoded.email, {
            token,
            expiration_date: expirationDate,
            tokenType: 'private'
          })
        )

        if (isEmbedded()) {
          postMessage(event.data)
        }

        return
      }

      if (type === 'redirectAndUpdateAuthToken') {
        const { token, expirationDate } = event.data
        if (!token || !expirationDate) return

        const decoded = jwtDecode<{ email: string }>(token)
        dispatch(
          loginSucceeded(decoded.email, {
            token,
            expiration_date: expirationDate,
            tokenType: 'private'
          })
        )

        if (isEmbedded()) {
          postMessage(event.data)
        } else {
          redirectToUrl(event.data.url)
        }

        return
      }

      if (type === 'getSharedState') {
        const { ownerUuid } = getApplicationSystemInfo()

        const applicationSystemOwner = { owner: ownerUuid }

        event.source?.postMessage(
          {
            originalSender,
            type: 'patchSharedState',
            sharedState: { ...sharedState, ...applicationSystemOwner }
          },
          { targetOrigin: '*' }
        )

        return
      }

      if (finishedInteraction && returnOnFinishedInteraction) {
        if (interactionReturnUrl) {
          redirectToUrl(interactionReturnUrl)
          return
        }

        navigate(-1)
      }
    },
    [
      origin,
      returnOnFinishedInteraction,
      postMessage,
      redirectToUrl,
      dispatch,
      sharedState,
      interactionReturnUrl,
      navigate
    ]
  )

  useEffect(() => {
    setIframeAttributes({
      src,
      height,
      title,
      allow
    })
  }, [allow, height, src, title])

  return {
    iframeAttributes,
    receiveMessage
  }
}

export default useIframe
